import React, { useState, useEffect, useMemo } from 'react';
import { apiUrl, authFetch, authService, ordersApi } from '../utils/api';
import { useUser } from '../contexts/UserContext';

interface Order {
  id: string;
  studentId: string;
  studentName: string | null;
  studentPhoneNumber: string | null;
  courseId: string;
  courseName: string | null;
  promoCodeId: string | null;
  promoCode: string | null;
  originalPrice: number;
  discountAmount: number;
  netPrice: number;
  orderDate: string;
  status: 'Pending' | 'Completed' | 'Cancelled';
  paymentReference: string | null;
  paymentDate: string | null;
}

interface SortConfig {
  key: keyof Order;
  direction: 'asc' | 'desc';
}

const Orders: React.FC = () => {
  const { isModerator } = useUser();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [studentNameFilter, setStudentNameFilter] = useState<string>('');
  const [studentPhoneFilter, setStudentPhoneFilter] = useState<string>('');
  const [startDateFilter, setStartDateFilter] = useState<string>('');
  const [endDateFilter, setEndDateFilter] = useState<string>('');
  const [promoCodeFilter, setPromoCodeFilter] = useState<string>('');
  const [orderIdFilter, setOrderIdFilter] = useState<string>('');
  const [orderToUpdate, setOrderToUpdate] = useState<Order | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // Fetch orders from API
  const fetchOrders = async () => {
    if (!isModerator()) {
      setError('You do not have permission to view orders.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const filters: Record<string, string> = {};

      if (orderIdFilter.trim()) {
        filters.orderId = orderIdFilter.trim();
      }
      
      if (statusFilter) {
        filters.status = statusFilter;
      }
      
      if (studentNameFilter) {
        filters.studentName = studentNameFilter;
      }
      
      if (studentPhoneFilter) {
        filters.studentPhoneNumber = studentPhoneFilter;
      }

      if (promoCodeFilter.trim()) {
        filters.promoCode = promoCodeFilter.trim();
      }
      
      if (startDateFilter) {
        filters.startDate = startDateFilter;
      }
      
      if (endDateFilter) {
        filters.endDate = endDateFilter;
      }
      
      const response = await ordersApi.getAllOrders(
        currentPage,
        itemsPerPage,
        Object.keys(filters).length > 0 ? filters : undefined
      );

      if (response.success) {
        const orderData = response.data;
        setOrders(orderData.data || []);
        setTotalCount(orderData.totalCount || 0);
        setTotalPages(orderData.totalPages || 0);
      } else {
        throw new Error(response.message || 'Failed to fetch orders');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Failed to load orders. Please try again later.');
      setOrders([]);
      setTotalPages(0);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [currentPage]);

  // Sorting logic
  const handleSort = (key: keyof Order) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Apply sorting (filtering is now done on backend)
  const sortedOrders = useMemo(() => {
    let sortableOrders = [...orders];
    
    // Apply sorting
    if (sortConfig !== null) {
      sortableOrders.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return sortableOrders;
  }, [orders, sortConfig]);

  // Handle page change
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  // Handle status filter change
  const handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
  };

  // Handle student name filter change
  const handleStudentNameFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStudentNameFilter(e.target.value);
  };

  // Handle student phone filter change
  const handleStudentPhoneFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStudentPhoneFilter(e.target.value);
  };

  // Handle start date filter change
  const handleStartDateFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDateFilter(e.target.value);
  };

  // Handle end date filter change
  const handleEndDateFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDateFilter(e.target.value);
  };

  const handlePromoCodeFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPromoCodeFilter(e.target.value);
  };

  const handleOrderIdFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderIdFilter(e.target.value);
  };

  // Apply filters
  const handleApplyFilters = () => {
    setCurrentPage(1);
    fetchOrders();
  };

  // Reset all filters
  const handleResetFilters = async () => {
    setStatusFilter('');
    setStudentNameFilter('');
    setStudentPhoneFilter('');
    setPromoCodeFilter('');
    setOrderIdFilter('');
    setStartDateFilter('');
    setEndDateFilter('');
    setCurrentPage(1);
    
    // Fetch orders with empty filters
    setLoading(true);
    setError(null);
    try {
      const response = await ordersApi.getAllOrders(1, itemsPerPage, undefined);
      
      if (response.success) {
        const orderData = response.data;
        setOrders(orderData.data || []);
        setTotalCount(orderData.totalCount || 0);
        setTotalPages(orderData.totalPages || 0);
      } else {
        throw new Error(response.message || 'Failed to fetch orders');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Failed to load orders. Please try again later.');
      setOrders([]);
      setTotalPages(0);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Handle status update
  const handleUpdateStatus = async (newStatus: 'Pending' | 'Completed' | 'Cancelled') => {
    if (!orderToUpdate) return;

    setIsUpdating(true);
    setError(null);
    try {
      const response = await ordersApi.updateOrderStatus(orderToUpdate.id, newStatus);
      
      if (response.success) {
        // Refresh orders list
        await fetchOrders();
        setOrderToUpdate(null);
      } else {
        throw new Error(response.message || 'Failed to update order status');
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      setError('Failed to update order status. Please try again later.');
    } finally {
      setIsUpdating(false);
    }
  };

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EGP',
      minimumFractionDigits: 2
    }).format(amount);
  };

  if (!isModerator()) {
    return (
      <div className="p-4 md:p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">You do not have permission to view orders.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6" dir="rtl">
      <h1 className="text-xl md:text-2xl font-bold mb-4 md:mb-6">الطلبات</h1>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 rounded-lg bg-red-50 p-4 border border-red-200">
          <div className="flex">
            <div className="mr-3">
              <h3 className="text-sm font-medium text-red-800">خطأ</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <h2 className="text-lg font-semibold mb-4">التصفية</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-7 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">order id</label>
            <input
              type="text"
              value={orderIdFilter}
              onChange={handleOrderIdFilterChange}
              placeholder="تصفية برقم الطلب..."
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary font-mono text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">الحالة</label>
            <select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            >
              <option value="">جميع الحالات</option>
              <option value="Pending">قيد الانتظار</option>
              <option value="Completed">مكتمل</option>
              <option value="Cancelled">ملغي</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">اسم الطالب</label>
            <input
              type="text"
              value={studentNameFilter}
              onChange={handleStudentNameFilterChange}
              placeholder="البحث بالاسم..."
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">رقم الهاتف</label>
            <input
              type="text"
              value={studentPhoneFilter}
              onChange={handleStudentPhoneFilterChange}
              placeholder="البحث برقم الهاتف..."
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">كود الخصم</label>
            <input
              type="text"
              value={promoCodeFilter}
              onChange={handlePromoCodeFilterChange}
              placeholder="مثال: SUMMER"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">من تاريخ</label>
            <input
              type="date"
              value={startDateFilter}
              onChange={handleStartDateFilterChange}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">إلى تاريخ</label>
            <input
              type="date"
              value={endDateFilter}
              onChange={handleEndDateFilterChange}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap justify-end gap-3">
          <button
            type="button"
            onClick={handleResetFilters}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            إعادة تعيين الفلاتر
          </button>
          <button
            type="button"
            onClick={handleApplyFilters}
            className="px-6 py-2 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors shadow-sm hover:shadow-md flex items-center gap-2"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            تطبيق الفلاتر
          </button>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b">
          <span className="font-medium">الإجمالي: {totalCount}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {[
                  { key: 'id' as const, label: 'رقم الطلب' },
                  { key: 'studentName' as const, label: 'اسم الطالب' },
                  { key: 'studentPhoneNumber' as const, label: 'رقم الهاتف' },
                  { key: 'courseName' as const, label: 'اسم الدورة' },
                  { key: 'promoCode' as const, label: 'كود الخصم' },
                  { key: 'originalPrice' as const, label: 'السعر الأصلي' },
                  { key: 'discountAmount' as const, label: 'مبلغ الخصم' },
                  { key: 'netPrice' as const, label: 'السعر النهائي' },
                  { key: 'orderDate' as const, label: 'تاريخ الطلب' },
                  { key: 'status' as const, label: 'الحالة' },
                  { key: 'paymentReference' as const, label: 'مرجع الدفع' },
                  { key: 'paymentDate' as const, label: 'تاريخ الدفع' },
                  { key: 'actions' as const, label: 'الإجراءات' }
                ].map(({ key, label }) => {
                  const isActions = key === 'actions';
                  return (
                    <th
                      key={key}
                      onClick={() => !isActions && handleSort(key as keyof Order)}
                      className={`px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider ${
                        !isActions ? 'cursor-pointer hover:bg-gray-100 transition-colors' : ''
                      }`}
                    >
                      {label}
                      {sortConfig?.key === key && !isActions && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={13} className="px-6 py-12">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  </td>
                </tr>
              ) : sortedOrders.length === 0 ? (
                <tr>
                  <td colSpan={13} className="px-6 py-4 text-center text-gray-500">
                    لا توجد طلبات
                  </td>
                </tr>
              ) : (
                sortedOrders.map((order: Order) => (
                  <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-sm">
                      {order.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{order.studentName || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{order.studentPhoneNumber || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{order.courseName || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {order.promoCode || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{formatCurrency(order.originalPrice)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                      {formatCurrency(order.discountAmount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold">
                      {formatCurrency(order.netPrice)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{formatDate(order.orderDate)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          order.status === 'Completed'
                            ? 'bg-green-100 text-green-800'
                            : order.status === 'Pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {order.status === 'Completed' ? 'مكتمل' : order.status === 'Pending' ? 'قيد الانتظار' : 'ملغي'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {order.paymentReference || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {formatDate(order.paymentDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => setOrderToUpdate(order)}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        تحديث الحالة
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && totalPages > 0 && (
          <div className="px-4 py-3 flex flex-col sm:flex-row items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="mb-4 sm:mb-0">
              <p className="text-sm text-gray-700">
                عرض <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> إلى{' '}
                <span className="font-medium">
                  {Math.min(currentPage * itemsPerPage, totalCount)}
                </span>{' '}
                من <span className="font-medium">{totalCount}</span> نتيجة
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span className="sr-only">الأول</span>
                  <span>&laquo;</span>
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span className="sr-only">السابق</span>
                  <span>&lsaquo;</span>
                </button>
                
                {/* Page numbers */}
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => 
                    page === 1 || 
                    page === totalPages || 
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  )
                  .map((page, index, array) => {
                    const prevPage = array[index - 1];
                    if (index > 0 && prevPage !== page - 1) {
                      return [
                        <span key={`ellipsis-${prevPage}`} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          ...
                        </span>,
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${
                            currentPage === page ? 'z-10 bg-primary text-white border-primary' : 'bg-white text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      ];
                    }
                    
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${
                          currentPage === page ? 'z-10 bg-primary text-white border-primary' : 'bg-white text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  }).flat()}
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span className="sr-only">التالي</span>
                  <span>&rsaquo;</span>
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span className="sr-only">الأخير</span>
                  <span>&raquo;</span>
                </button>
              </nav>
            </div>
          </div>
        )}
      </div>

      {/* Update Status Modal */}
      {orderToUpdate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md border border-gray-200">
            <div className="text-center mb-5">
              <h3 className="text-xl font-medium text-gray-900 mb-2">تحديث حالة الطلب</h3>
              <p className="text-gray-600">
                الطلب: {orderToUpdate.id}<br />
                الحالة الحالية: {orderToUpdate.status === 'Completed' ? 'مكتمل' : orderToUpdate.status === 'Pending' ? 'قيد الانتظار' : 'ملغي'}
              </p>
            </div>
            <div className="mt-6 space-y-3">
              <button
                onClick={() => handleUpdateStatus('Pending')}
                disabled={isUpdating || orderToUpdate.status === 'Pending'}
                className={`w-full px-4 py-2 rounded-lg text-white font-medium transition-all ${
                  orderToUpdate.status === 'Pending'
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-yellow-600 hover:bg-yellow-700'
                } disabled:opacity-50`}
              >
                قيد الانتظار
              </button>
              <button
                onClick={() => handleUpdateStatus('Completed')}
                disabled={isUpdating || orderToUpdate.status === 'Completed'}
                className={`w-full px-4 py-2 rounded-lg text-white font-medium transition-all ${
                  orderToUpdate.status === 'Completed'
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700'
                } disabled:opacity-50`}
              >
                مكتمل
              </button>
              <button
                onClick={() => handleUpdateStatus('Cancelled')}
                disabled={isUpdating || orderToUpdate.status === 'Cancelled'}
                className={`w-full px-4 py-2 rounded-lg text-white font-medium transition-all ${
                  orderToUpdate.status === 'Cancelled'
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-red-600 hover:bg-red-700'
                } disabled:opacity-50`}
              >
                إلغاء
              </button>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setOrderToUpdate(null)}
                disabled={isUpdating}
                className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-all disabled:opacity-50"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
